import {Spot, TextTranslation} from "../types";

export type LocalizedContentState = "notTranslated" | "partiallyTranslated" | "fullyTranslated";

export const SPOT_TRANSLATION_FIELDS = [
    {
        translationKey: "spot.name",
        label: "Spot name",
        placeholder: "Translated spot name"
    },
    {
        translationKey: "spot.summary",
        label: "Summary",
        placeholder: "Translated summary"
    },
    {
        translationKey: "spot.description",
        label: "Description",
        inputType: "richText" as const,
        placeholder: "Translated description"
    }
];

export const normalizeLanguageCode = (value?: string) => (value || "").trim().split("-")[0].toUpperCase();

export const isMissingText = (value?: string) => !(value || "").trim();

export const buildSpotTranslationRowsByClientId = (translations: TextTranslation[]) => {
    const rowsByClientId = new Map<string, TextTranslation[]>();
    translations.forEach((translation) => {
        const rows = rowsByClientId.get(translation.clientId) || [];
        rows.push(translation);
        rowsByClientId.set(translation.clientId, rows);
    });
    return rowsByClientId;
};

export const getSpotTranslationState = (
    spot: Spot,
    rowsByClientId: Map<string, TextTranslation[]>,
    translatableLanguageCodes: string[],
): LocalizedContentState | undefined => {
    if (translatableLanguageCodes.length === 0) {
        return undefined;
    }

    const rows = rowsByClientId.get(spot.key) || [];
    const hasAnyTranslation = rows.some((row) =>
        SPOT_TRANSLATION_FIELDS.some((field) => field.translationKey === row.translationKey) &&
        !isMissingText(row.text)
    );
    const totalRequiredTranslations = SPOT_TRANSLATION_FIELDS.length * translatableLanguageCodes.length;
    const translatedEntries = translatableLanguageCodes.reduce((count, languageCode) => {
        return count + SPOT_TRANSLATION_FIELDS.filter((field) => {
            return rows.some((row) =>
                normalizeLanguageCode(row.languageCode) === languageCode &&
                row.translationKey === field.translationKey &&
                !isMissingText(row.text)
            );
        }).length;
    }, 0);

    if (!hasAnyTranslation) {
        return "notTranslated";
    }

    if (translatedEntries >= totalRequiredTranslations) {
        return "fullyTranslated";
    }

    return "partiallyTranslated";
};
